 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import { Clock, TrendingUp, TrendingDown, AlertCircle, Filter } from 'lucide-react';

interface EventLogSectionProps {
  events: unknown[];
  onEventAction: (eventId: string, action: string) => void;
  activeEvents?: unknown[];
  onNavigate?: () => void;
}

export function EventLogSection({ events, onEventAction, activeEvents = [], onNavigate }: EventLogSectionProps) {
  const [filter, setFilter] = useState<'all' | 'active' | 'recent'>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Helper to safely access event properties
  const getEventProp = (event: unknown, path: string): unknown => {
    if (!event || typeof event !== 'object') return undefined;
    const keys = path.split('.');
    let current: unknown = event;
    for (const key of keys) {
      if (!current || typeof current !== 'object') return undefined;
      current = (current as Record<string, unknown>)[key];
    }
    return current;
  };

  // Helper function to safely get timestamp from either event type
  const getEventTimestamp = (event: unknown): number => {
    const evt = event as Record<string, unknown>;
    // Try triggeredAt first (ActiveEvent)
    if (evt.triggeredAt instanceof Date) return evt.triggeredAt.getTime();
    if (evt.triggeredAt) {
      try {
        return new Date(evt.triggeredAt as string).getTime();
      } catch (e) {
        // Invalid date string
      }
    }
    
    // Try timestamp (GameEvent)
    if (evt.timestamp instanceof Date) return evt.timestamp.getTime();
    if (evt.timestamp) {
      try {
        return new Date(evt.timestamp as string).getTime();
      } catch (e) {
        // Invalid date string
      }
    }
    
    // Fallback to current time
    return Date.now();
  };

  // Separate active events (requiring decisions) from recent events
  const pendingEvents = (activeEvents || []).filter((e: unknown) => {
    const evt = e as Record<string, unknown>;
    return evt && evt.status === 'pending';
  });
  // Sort events by game date (newest first), then by timestamp
  const sortedEvents = [...(events || [])].sort((a, b) => {
    // Get game dates if available
    const gameDateA = getEventProp(a, 'gameDate');
    const gameDateB = getEventProp(b, 'gameDate');
    
    if (gameDateA && gameDateB && typeof gameDateA === 'object' && typeof gameDateB === 'object') {
      const gdA = gameDateA as { day: number; month: number; year: number };
      const gdB = gameDateB as { day: number; month: number; year: number };
      
      const dateA = new Date(gdA.year, gdA.month - 1, gdA.day);
      const dateB = new Date(gdB.year, gdB.month - 1, gdB.day);
      const gameDateDiff = dateB.getTime() - dateA.getTime();
      
      if (gameDateDiff !== 0) return gameDateDiff;
    }
    
    // If game dates are equal or not available, compare real timestamps
    const timestampA = getEventTimestamp(a);
    const timestampB = getEventTimestamp(b);
    return timestampB - timestampA;
  });
  
  const recentEvents = sortedEvents.slice(0, 10); // Show 10 most recent events

  const getEventIcon = (event: unknown) => {
    if (event && typeof event === 'object') {
      const evt = event as Record<string, unknown>;
      if ('status' in evt) {
        // ActiveEvent
        if (evt.status === 'pending') return <Clock className="w-4 h-4 text-theme-warning" />;
        const innerEvent = evt.event as Record<string, unknown> | undefined;
        if (innerEvent?.severity === 'high') return <AlertCircle className="w-4 h-4 text-theme-error" />;
      } else {
        // GameEvent
        if (evt.severity === 'error') return <TrendingDown className="w-4 h-4 text-theme-error" />;
        if (evt.severity === 'success') return <TrendingUp className="w-4 h-4 text-theme-success" />;
      }
    }
    return <AlertCircle className="w-4 h-4 text-theme-info" />;
  };

  const handleEventClick = (event: unknown) => {
    if (event && typeof event === 'object') {
      const evt = event as Record<string, unknown>;
      if ('status' in evt && evt.status === 'pending') {
        const innerEvent = evt.event as Record<string, unknown> | undefined;
        const eventId = (innerEvent?.id || evt.id) as string;
        onEventAction(eventId, 'view');
      }
    }
  };

  const formatGameDate = (gameDate: { day: number; month: number; year: number; hour?: number; minute?: number }) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[gameDate.month - 1] || 'Jan';
    const time = gameDate.hour !== undefined && gameDate.minute !== undefined 
      ? ` ${gameDate.hour.toString().padStart(2, '0')}:${gameDate.minute.toString().padStart(2, '0')}`
      : '';
    return `${month} ${gameDate.day}, ${gameDate.year}${time}`;
  };

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg overflow-hidden h-full flex flex-col">
      <div className="bg-theme-bg-content p-2 border-b border-theme-border flex-shrink-0 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={onNavigate}
            className={`font-bold text-sm text-theme-text-secondary ${
              onNavigate ? 'hover:text-theme-accent transition-colors cursor-pointer' : ''
            }`}
            disabled={!onNavigate}
          >
            Event Log / Decision Center
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-1 hover:bg-theme-bg-tertiary rounded"
          >
            <Filter className="w-3 h-3 text-theme-text-muted" />
          </button>
        </div>
        {showFilters && (
          <div className="flex gap-2 mt-2">
            {(['all', 'active', 'recent'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2 py-1 text-xs rounded ${
                  filter === f ? 'bg-theme-accent text-white' : 'bg-theme-bg-tertiary text-theme-text-secondary'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="p-3 flex-1 overflow-y-auto min-h-0 scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        {/* Pending Decisions */}
        {pendingEvents.length > 0 && (filter === 'all' || filter === 'active') && (
          <div className="mb-4">
            <div className="text-sm font-bold mb-2 text-theme-warning">Requires Decision</div>
            {pendingEvents.map((event, index) => (
              <div
                key={`${getEventProp(event, 'event.id') || getEventProp(event, 'id') || 'event'}-${getEventTimestamp(event)}-${getEventProp(event, 'gameMonth') || 'month'}-${getEventProp(event, 'productContext.productId') || 'global'}-pending-${index}`}
                onClick={() => handleEventClick(event)}
                className="bg-theme-warning/10 border border-theme-warning/30 rounded p-2 mb-2 cursor-pointer hover:bg-theme-warning/20 transition-colors"
              >
                <div className="flex items-start gap-2">
                  {getEventIcon(event)}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs text-theme-text-primary truncate">
                      {(getEventProp(event, 'event.title') || getEventProp(event, 'title') || 'Event') as string}
                    </div>
                    <div className="text-xs text-theme-text-secondary line-clamp-2">
                      {(getEventProp(event, 'event.description') || getEventProp(event, 'description') || 'No description') as string}
                    </div>
                    {(() => {
                      const choices = getEventProp(event, 'event.choices') as Array<unknown> | undefined;
                      return choices ? (
                        <div className="text-xs text-theme-warning mt-1">
                          {choices.length} options available
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recent Events */}
        {recentEvents.length > 0 && (filter === 'all' || filter === 'recent') && (
          <div>
            <div className="text-sm font-bold mb-2 text-theme-text-secondary">Recent Activity</div>
            {recentEvents.map((event, index) => (
              <div
                key={`${getEventProp(event, 'event.id') || getEventProp(event, 'id') || 'event'}-${getEventTimestamp(event)}-${getEventProp(event, 'gameMonth') || 'month'}-${getEventProp(event, 'productContext.productId') || 'global'}-recent-${index}`}
                className="bg-theme-bg-content/80 rounded p-2 mb-2"
              >
                <div className="flex items-start gap-2">
                  {getEventIcon(event)}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs text-theme-text-primary truncate">
                      {(getEventProp(event, 'event.title') || getEventProp(event, 'title') || 'Event') as string}
                    </div>
                    <div className="text-xs text-theme-text-secondary line-clamp-2">
                      {(getEventProp(event, 'event.description') || getEventProp(event, 'description') || 'No description') as string}
                    </div>
                    <div className="text-xs text-theme-text-muted mt-1">
                      {(() => {
                        const gameDate = getEventProp(event, 'gameDate');
                        if (gameDate && typeof gameDate === 'object') {
                          return formatGameDate(gameDate as { day: number; month: number; year: number; hour?: number; minute?: number });
                        }
                        const triggeredAt = getEventProp(event, 'triggeredAt');
                        if (triggeredAt) return new Date(triggeredAt as string | Date).toLocaleTimeString();
                        const timestamp = getEventProp(event, 'timestamp');
                        if (timestamp) return new Date(timestamp as string | Date).toLocaleTimeString();
                        return 'Unknown time';
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {pendingEvents.length === 0 && recentEvents.length === 0 && (
          <div className="text-center text-theme-text-muted text-sm mt-8">
            No events recorded yet
          </div>
        )}
      </div>
    </div>
  );
}